/**
 * Role-Based Access Control Utility
 * Centralized permission checking for MyBodyToday platform
 */

// Officer role identifier
export const OFFICER_ROLE = 'caregiver';

// Define officer-exclusive features
export const OFFICER_FEATURES = {
  BUSINESS_INTELLIGENCE: 'business_intelligence',
  MASTER_ANALYTICS: 'master_analytics',
  USER_ENGAGEMENT_ANALYTICS: 'user_engagement_analytics',
  CONVERSION_FUNNEL_ANALYTICS: 'conversion_funnel_analytics',
  AUDIT_TRAIL: 'audit_trail',
  DEBUG_DASHBOARD: 'debug_dashboard',
  SESSION_MANAGEMENT: 'session_management',
  CRITICAL_ALERTS: 'critical_alerts',
  CAREGIVER_ANALYTICS: 'caregiver_analytics',
  MASTER_DASHBOARD: 'master_dashboard'
};

// Feature display names for UI
export const FEATURE_NAMES = {
  [OFFICER_FEATURES?.BUSINESS_INTELLIGENCE]: 'Business Intelligence Dashboard',
  [OFFICER_FEATURES?.MASTER_ANALYTICS]: 'Master Analytics Dashboard',
  [OFFICER_FEATURES?.USER_ENGAGEMENT_ANALYTICS]: 'User Engagement Analytics',
  [OFFICER_FEATURES?.CONVERSION_FUNNEL_ANALYTICS]: 'Premium Conversion Funnel',
  [OFFICER_FEATURES?.AUDIT_TRAIL]: 'Audit Trail Dashboard',
  [OFFICER_FEATURES?.DEBUG_DASHBOARD]: 'Debug Dashboard',
  [OFFICER_FEATURES?.SESSION_MANAGEMENT]: 'Session Management',
  [OFFICER_FEATURES?.CRITICAL_ALERTS]: 'Critical Alerts Dashboard',
  [OFFICER_FEATURES?.CAREGIVER_ANALYTICS]: 'Caregiver Analytics Dashboard',
  [OFFICER_FEATURES?.MASTER_DASHBOARD]: 'MyBodyToday Officers Master Dashboard'
};

/**
 * Check if user has officer role based on email domain
 * @param {Object} userProfile - User profile object from AuthContext
 * @param {Object} user - User object from AuthContext (contains email)
 * @returns {boolean} - True if user is an officer
 */
export const isOfficer = (userProfile, user) => {
  // Check email domain for officer identification
  const email = user?.email || userProfile?.email;
  if (!email) return false;
  
  const emailLower = email?.toLowerCase();
  return emailLower?.includes('@mybodytoday.') ||
    emailLower?.includes('@mybodytoday1.') ||
    emailLower === 'owner@mybodytoday.com' ||
    emailLower === 'admin@mybodytoday.com' ||
    emailLower === 'owner@mybodytoday1.com' ||
    emailLower === 'admin@mybodytoday1.com' ||
    emailLower === 'kingsley@mybodytoday1.com'; // Owner account with full access
};

/**
 * Check if user is the owner account (bypasses all restrictions)
 * @param {Object} user - User object from AuthContext (contains email)
 * @returns {boolean} - True if user is the owner
 */
export const isOwner = (user) => {
  const email = user?.email;
  if (!email) return false;
  return email?.toLowerCase() === 'kingsley@mybodytoday1.com';
};

/**
 * Check if user has access to a specific feature
 * @param {Object} userProfile - User profile object from AuthContext
 * @param {string} feature - Feature identifier from OFFICER_FEATURES
 * @param {Object} user - User object from AuthContext (optional, for email-based checks)
 * @returns {boolean} - True if user has access
 */
export const hasFeatureAccess = (userProfile, feature, user = null) => {
  // Owner has unrestricted access to everything
  if (isOwner(user)) {
    return true;
  }
  
  // Officers have access to all officer-exclusive features
  if (isOfficer(userProfile, user)) {
    return true;
  }
  
  // Non-officers don't have access to officer features
  return false;
};

/**
 * Get permission denial reason for UI display
 * @param {string} feature - Feature identifier
 * @returns {string} - Human-readable denial reason
 */
export const getPermissionDenialReason = (feature) => {
  const featureName = FEATURE_NAMES?.[feature] || 'This feature';
  return `${featureName} is restricted to MyBodyToday officers only.`;
};

/**
 * Check multiple features at once
 * @param {Object} userProfile - User profile object
 * @param {Array<string>} features - Array of feature identifiers
 * @param {Object} user - User object from AuthContext (optional)
 * @returns {Object} - Map of feature to access boolean
 */
export const checkMultipleFeatures = (userProfile, features, user = null) => {
  return features?.reduce((acc, feature) => {
    acc[feature] = hasFeatureAccess(userProfile, feature, user);
    return acc;
  }, {});
};
